import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ShieldAlert, Search, Plus, Trash2, Loader2, Database,
  X, Download, RefreshCw, Bell, AlertTriangle, Zap, PlayCircle,
  Clock, CheckCircle2, XCircle, FileText, ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import { CollectorFreshnessBanner } from "../components/CollectorFreshnessBanner";
import { SkeletonTable } from "../components/Skeleton";
import IdentityResultCard from "../components/IdentityResultCard";
import { fromWatchlist } from "../lib/identityNormalizers";

const API = `${window.location.origin}/api/watchlist`;

const SOURCE_COLORS = {
  SEBI:          "#06b6d4",
  MHA_UAPA:      "#dc2626",
  ED:            "#ef4444",
  RBI:           "#f59e0b",
  SFIO:          "#fbbf24",
  CBI:           "#8b5cf6",
  NSE:           "#22c55e",
  ICAI:          "#64748b",
  OPENSANCTIONS: "#a855f7",
  GSTIN:         "#10b981",
  MCA:           "#3b82f6",
};

const SEVERITY_COLORS = {
  critical: "#dc2626",
  high:     "#f97316",
  medium:   "#f59e0b",
};

const COLLECTOR_SOURCES = ["SEBI", "OPENSANCTIONS", "ENFORCEMENT", "GSTIN", "MCA"];
const ALL_SOURCES = ["SEBI", "MHA_UAPA", "ED", "RBI", "SFIO", "CBI", "NSE", "ICAI", "OPENSANCTIONS", "GSTIN", "MCA"];

const inputStyle = { width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: "6px", padding: "8px 12px", color: "#f1f5f9", fontSize: "12px" };
const selectStyle = { ...inputStyle, cursor: "pointer" };
const btnPrimary = { background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: "6px", color: "white", padding: "8px 18px", fontWeight: 600, fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" };
const btnGhost = { background: "transparent", border: "1px solid #1e2530", borderRadius: "6px", color: "#94a3b8", padding: "8px 14px", fontSize: "12px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" };

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: "#0d1117", border: `1px solid ${color}33`, borderRadius: "10px", padding: "14px" }}>
      <div style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "4px" }}>{label}</div>
      <div style={{ color, fontSize: "24px", fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <div style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>{label}</div>
      {children}
    </div>
  );
}

function SeverityBadge({ severity }) {
  const c = SEVERITY_COLORS[severity] || "#64748b";
  return (
    <span style={{ background: `${c}22`, color: c, border: `1px solid ${c}55`, fontSize: "10px", padding: "2px 8px", borderRadius: "4px", fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase" }}>{severity}</span>
  );
}

function SourceBadge({ source }) {
  const c = SOURCE_COLORS[source] || "#64748b";
  return (
    <span style={{ background: `${c}22`, color: c, border: `1px solid ${c}55`, fontSize: "10px", padding: "2px 8px", borderRadius: "4px", fontWeight: 700, letterSpacing: "0.4px" }}>{source}</span>
  );
}

function ScoreBar({ score }) {
  const c = score >= 95 ? "#dc2626" : score >= 88 ? "#f97316" : score >= 80 ? "#f59e0b" : "#22c55e";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 110 }}>
      <div style={{ flex: 1, height: 6, background: "#1e2530", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${score}%`, height: "100%", background: c }} />
      </div>
      <strong style={{ color: c, fontSize: 12, minWidth: 32, textAlign: "right" }}>{score}%</strong>
    </div>
  );
}

// =====================================================
// ============== ADD ENTRY MODAL ======================
// =====================================================
function AddEntryModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ source: "SEBI", entity_name: "", entity_type: "INDIVIDUAL", pan: "", cin: "", reason: "", order_date: "", order_ref: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.entity_name.trim()) { toast.error("Entity name required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/entries`, form, { withCredentials: true });
      toast.success("Entry added");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "24px", width: "100%", maxWidth: "600px" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ color: "#f1f5f9", fontSize: 16, fontWeight: 700, margin: 0 }}>Add Watchlist Entry</h3>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}><X size={18} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <Field label="Source"><select data-testid="watchlist-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} style={selectStyle}>{ALL_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
          <Field label="Entity Type"><select data-testid="watchlist-type" value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} style={selectStyle}><option value="INDIVIDUAL">Individual</option><option value="COMPANY">Company</option></select></Field>
          <Field label="Entity Name" full><input data-testid="watchlist-name" type="text" value={form.entity_name} onChange={(e) => setForm({ ...form, entity_name: e.target.value })} style={inputStyle} placeholder="Full legal name" /></Field>
          <Field label="PAN"><input type="text" value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} style={inputStyle} maxLength={10} /></Field>
          <Field label="CIN"><input type="text" value={form.cin} onChange={(e) => setForm({ ...form, cin: e.target.value.toUpperCase() })} style={inputStyle} maxLength={21} /></Field>
          <Field label="Reason" full><input type="text" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} style={inputStyle} /></Field>
          <Field label="Order Date"><input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} style={inputStyle} /></Field>
          <Field label="Order Ref"><input type="text" value={form.order_ref} onChange={(e) => setForm({ ...form, order_ref: e.target.value })} style={inputStyle} /></Field>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="watchlist-save-btn" style={btnPrimary}>{saving ? <Loader2 className="animate-spin" size={12} /> : <Plus size={12} />}{saving ? "Saving..." : "Add Entry"}</button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// ============== ENTRIES TAB ==========================
// =====================================================
function EntriesTab({ stats, entries, loading, searchQ, setSearchQ, sourceFilter, setSourceFilter, typeFilter, setTypeFilter, onDelete, onExport, onSeedDefaults, seeding }) {
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: "10px", marginBottom: "16px" }}>
        <StatCard label="Total" value={stats.total} color="#2563eb" />
        {ALL_SOURCES.map((s) => (<StatCard key={s} label={s.replace("_", " ")} value={stats.by_source?.[s] || 0} color={SOURCE_COLORS[s]} />))}
      </div>
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "14px", marginBottom: "16px" }}>
        <div className="flex gap-3 items-center">
          <div style={{ position: "relative", flex: 1 }}>
            <Search style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#64748b", width: 14, height: 14 }} />
            <input type="text" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search entity name (text index — fast, min 2 chars)..." data-testid="watchlist-search-input"
              style={{ ...inputStyle, padding: "8px 12px 8px 32px", fontSize: 13 }} />
          </div>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} data-testid="watchlist-source-filter" style={{ ...selectStyle, width: 170 }}>
            <option value="">All sources</option>{ALL_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="watchlist-type-filter" style={{ ...selectStyle, width: 140 }}>
            <option value="">All types</option><option value="INDIVIDUAL">Individual</option><option value="COMPANY">Company</option>
          </select>
          <button onClick={onExport} data-testid="watchlist-export-btn" style={btnGhost}><Download size={12} /> CSV</button>
        </div>
      </div>
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#080c12" }}><Th>Source</Th><Th>Entity Name</Th><Th>Type</Th><Th>PAN / CIN</Th><Th>Reason</Th><Th>Order Date</Th><Th>Order Ref</Th><Th></Th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 0 }}>
                <SkeletonTable rows={6} cols={8} />
              </td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 0 }}>
                <div data-testid="watchlist-empty-state" style={{
                  textAlign: "center", padding: "48px 24px",
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: "50%",
                    background: "rgba(37,99,235,0.08)", border: "1px solid #2563eb55",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Database size={28} style={{ color: "#2563eb" }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9", margin: 0, marginBottom: 4 }}>
                      Watchlist is empty
                    </h3>
                    <p style={{ fontSize: 12, color: "#94a3b8", margin: 0, maxWidth: 460 }}>
                      Collector hasn't run yet. Seed the default SEBI, MHA UAPA, ED, RBI, CBI, SFIO,
                      OpenSanctions, MCA and GSTIN lists to start screening.
                    </p>
                  </div>
                  <button
                    onClick={onSeedDefaults}
                    disabled={seeding}
                    data-testid="watchlist-empty-seed-btn"
                    style={{
                      background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                      border: "none", borderRadius: 8,
                      padding: "12px 28px", color: "#fff",
                      fontSize: 13, fontWeight: 700, cursor: seeding ? "not-allowed" : "pointer",
                      display: "inline-flex", alignItems: "center", gap: 8,
                      boxShadow: "0 6px 24px rgba(37,99,235,0.3)",
                    }}
                  >
                    {seeding ? <Loader2 className="animate-spin" size={14} /> : <Database size={14} />}
                    {seeding ? "Seeding defaults…" : "Seed Default Watchlists"}
                  </button>
                  <span style={{ fontSize: 10, color: "#475569" }}>
                    Or use the Collectors tab to run a specific source.
                  </span>
                </div>
              </td></tr>
            ) : entries.map((e) => (
              <tr key={e.id} style={{ borderTop: "1px solid #1e2530" }} data-testid={`watchlist-row-${e.id}`}>
                <Td><SourceBadge source={e.source} /></Td>
                <Td><strong style={{ color: "#f1f5f9" }}>{e.entity_name}</strong></Td>
                <Td><span style={{ color: "#94a3b8", fontSize: 11 }}>{e.entity_type}</span></Td>
                <Td><span style={{ color: "#94a3b8", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{e.pan || e.cin || "—"}</span></Td>
                <Td><span style={{ color: "#cbd5e1", fontSize: 12 }}>{e.reason || "—"}</span></Td>
                <Td><span style={{ color: "#94a3b8", fontSize: 11 }}>{e.order_date || "—"}</span></Td>
                <Td><span style={{ color: "#94a3b8", fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{e.order_ref || "—"}</span></Td>
                <Td><button onClick={() => onDelete(e.id)} data-testid={`watchlist-delete-${e.id}`} style={{ background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", borderRadius: "4px", color: "#ef4444", padding: "4px 8px", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}><Trash2 size={10} /></button></Td>
              </tr>
            ))}
          </tbody>
        </table>
        {entries.length > 0 && <div style={{ padding: "10px 14px", background: "#080c12", color: "#64748b", fontSize: 11, borderTop: "1px solid #1e2530" }}>Showing {entries.length} entries {searchQ ? `· matched "${searchQ}"` : ""}</div>}
      </div>
    </>
  );
}

// =====================================================
// ============== FUZZY SCREEN TAB =====================
// =====================================================
function FuzzyScreenTab({ refreshAlerts }) {
  const [form, setForm] = useState({ name: "", pan: "", cin: "", customer_id: "", threshold: 80 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const run = async (createAlert = false) => {
    if (!form.name.trim() || form.name.trim().length < 2) { toast.error("Name required (min 2 chars)"); return; }
    (createAlert ? setCreating : setLoading)(true);
    try {
      const { data } = await axios.post(`${API}/fuzzy-screen`, { ...form, create_alert: createAlert }, { withCredentials: true });
      setResult(data);
      if (createAlert && data.alert?.alert_id) {
        toast.success(data.alert.case_number ? `Alert + Case ${data.alert.case_number} created` : "Alert created");
        refreshAlerts?.();
      } else if (createAlert && data.alert?.skipped === "duplicate_today") {
        toast.info("Alert already exists for today");
      } else if (createAlert) {
        toast.info("No matches above threshold");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Screen failed"); }
    finally { (createAlert ? setCreating : setLoading)(false); }
  };

  return (
    <>
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 18, marginBottom: 16 }}>
        <div className="flex items-center gap-3 mb-4">
          <Zap size={18} style={{ color: "#facc15" }} />
          <h3 style={{ color: "#f1f5f9", margin: 0, fontSize: 14, fontWeight: 700 }}>Fuzzy Watchlist Screening</h3>
          <span style={{ color: "#64748b", fontSize: 11 }}>Handles spelling variations · "Mehul Choski" ≈ "Mehul Choksi" · "Vijai Malya" ≈ "Vijay Mallya"</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 100px", gap: 10 }}>
          <Field label="Customer Name (required)" full={false}>
            <input data-testid="fuzzy-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Mehul Choksy" style={inputStyle} onKeyDown={(e) => e.key === "Enter" && run(false)} />
          </Field>
          <Field label="PAN (optional)"><input type="text" value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} style={inputStyle} maxLength={10} placeholder="ABCDE1234F" /></Field>
          <Field label="CIN (optional)"><input type="text" value={form.cin} onChange={(e) => setForm({ ...form, cin: e.target.value.toUpperCase() })} style={inputStyle} maxLength={21} /></Field>
          <Field label="Threshold %">
            <input type="number" min={50} max={100} value={form.threshold} onChange={(e) => setForm({ ...form, threshold: parseInt(e.target.value) || 80 })} style={inputStyle} data-testid="fuzzy-threshold" />
          </Field>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2">
            <Field label="Customer ID (optional — links alert to existing customer)" full>
              <input type="text" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} style={{ ...inputStyle, width: 320 }} placeholder="cust-xxxx (optional)" />
            </Field>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="fuzzy-run-btn" onClick={() => run(false)} disabled={loading} style={btnGhost}>
              {loading ? <Loader2 className="animate-spin" size={12} /> : <Search size={12} />}
              {loading ? "Scanning..." : "Preview Matches"}
            </button>
            <button data-testid="fuzzy-create-alert-btn" onClick={() => run(true)} disabled={creating} style={btnPrimary}>
              {creating ? <Loader2 className="animate-spin" size={12} /> : <Bell size={12} />}
              {creating ? "Creating..." : "Screen & Raise Alert"}
            </button>
          </div>
        </div>
      </div>

      {result && (
        <>
        <div style={{ marginBottom: 16 }}>
          <IdentityResultCard
            person={fromWatchlist(
              (result.matches && result.matches[0]) || null,
              result.query?.name || form.name
            )}
          />
        </div>
        <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 16 }}>
          <div className="flex items-center justify-between mb-3">
            <div style={{ color: "#cbd5e1", fontSize: 13 }}>
              <strong style={{ color: result.count ? "#f97316" : "#22c55e" }}>{result.count}</strong> match(es) for <code style={{ color: "#f1f5f9", background: "#080c12", padding: "2px 6px", borderRadius: 4 }}>{result.query.name}</code> @ ≥{result.query.threshold}%
            </div>
            {result.alert?.alert_id && (
              <span style={{ color: "#22c55e", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <CheckCircle2 size={14} /> Alert raised {result.alert.case_number ? `· Case ${result.alert.case_number}` : ""}
              </span>
            )}
          </div>
          {result.matches.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#64748b", fontSize: 12 }}>No matches — name is clean against current watchlist.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ background: "#080c12" }}><Th>Score</Th><Th>Source</Th><Th>Matched Entity</Th><Th>PAN / CIN</Th><Th>Reason</Th><Th>Basis</Th></tr></thead>
              <tbody>
                {result.matches.map((m) => (
                  <tr key={m.id} style={{ borderTop: "1px solid #1e2530" }} data-testid={`fuzzy-match-${m.id}`}>
                    <Td><ScoreBar score={m.score} /></Td>
                    <Td><SourceBadge source={m.source} /></Td>
                    <Td><strong style={{ color: "#f1f5f9" }}>{m.entity_name}</strong></Td>
                    <Td><span style={{ color: "#94a3b8", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{m.pan || m.cin || "—"}</span></Td>
                    <Td><span style={{ color: "#cbd5e1", fontSize: 11 }}>{m.reason || "—"}</span></Td>
                    <Td><span style={{ color: m.match_basis === "name_fuzzy" ? "#a855f7" : "#22c55e", fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>{m.match_basis?.replace("_", " ")}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>
      )}
    </>
  );
}

// =====================================================
// ============== COLLECTORS TAB =======================
// =====================================================
function CollectorsTab() {
  const [status, setStatus] = useState({ sources: [], history: [] });
  const [running, setRunning] = useState(null); // null or source key
  const [loadingStatus, setLoadingStatus] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoadingStatus(true);
    try {
      const { data } = await axios.get(`${API}/collectors/status`, { withCredentials: true });
      setStatus(data);
    } catch (err) { console.error("[Watchlist] Failed to load collector status:", err); }
    finally { setLoadingStatus(false); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runOne = async (source) => {
    setRunning(source || "ALL");
    try {
      const body = source ? { source } : {};
      const { data } = await axios.post(`${API}/collectors/run`, body, { withCredentials: true });
      toast.success(`Inserted ${data.total_inserted} new entries${data.total_alerts ? `, ${data.total_alerts} alerts` : ""}`);
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Run failed"); }
    finally { setRunning(null); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : "Never";

  return (
    <>
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 18, marginBottom: 16 }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <PlayCircle size={18} style={{ color: "#22c55e" }} />
            <h3 style={{ color: "#f1f5f9", margin: 0, fontSize: 14, fontWeight: 700 }}>Automated Collectors</h3>
            <span style={{ color: "#64748b", fontSize: 11 }}>Background scheduler pulls SEBI · OpenSanctions · ED/CBI/SFIO · GSTIN · MCA every 6h</span>
          </div>
          <button data-testid="run-all-collectors-btn" onClick={() => runOne(null)} disabled={running} style={btnPrimary}>
            {running === "ALL" ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
            {running === "ALL" ? "Running All..." : "Run All Now"}
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
          {status.sources.map((s) => (
            <div key={s.source} style={{ background: "#080c12", border: `1px solid ${SOURCE_COLORS[s.source] || "#1e2530"}55`, borderRadius: 8, padding: 14 }} data-testid={`collector-card-${s.source}`}>
              <div className="flex items-center justify-between mb-2">
                <SourceBadge source={s.source} />
                {s.last_status === "ok" ? <CheckCircle2 size={14} style={{ color: "#22c55e" }} />
                  : s.last_status === "error" ? <XCircle size={14} style={{ color: "#ef4444" }} />
                  : <Clock size={14} style={{ color: "#64748b" }} />}
              </div>
              <div style={{ color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, marginBottom: 2 }}>Last Run</div>
              <div style={{ color: "#cbd5e1", fontSize: 11, marginBottom: 8 }}>{fmtDate(s.last_run_at)}</div>
              <div className="flex items-center justify-between" style={{ fontSize: 11 }}>
                <span style={{ color: "#94a3b8" }}>+{s.last_inserted} entries · {s.last_alerts} alerts</span>
                <button onClick={() => runOne(s.source)} disabled={running} style={{ ...btnGhost, padding: "4px 10px", fontSize: 10 }} data-testid={`run-collector-${s.source}`}>
                  {running === s.source ? <Loader2 className="animate-spin" size={10} /> : <PlayCircle size={10} />}
                  Run
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ padding: "12px 14px", background: "#080c12", borderBottom: "1px solid #1e2530", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <strong style={{ color: "#f1f5f9", fontSize: 13 }}>Run History</strong>
          <button onClick={loadStatus} style={btnGhost}><RefreshCw size={11} className={loadingStatus ? "animate-spin" : ""} /> Refresh</button>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#080c12" }}><Th>Source</Th><Th>Mode</Th><Th>Started</Th><Th>Duration</Th><Th>Fetched</Th><Th>Inserted</Th><Th>Alerts</Th><Th>Status</Th></tr></thead>
          <tbody>
            {status.history.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 30, color: "#64748b", fontSize: 12 }}>No runs yet. Click "Run All Now".</td></tr>
            ) : status.history.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid #1e2530" }} data-testid={`run-row-${r.id}`}>
                <Td><SourceBadge source={r.source} /></Td>
                <Td><span style={{ background: r.mode === "live" ? "rgba(34,197,94,0.15)" : "rgba(148,163,184,0.15)", color: r.mode === "live" ? "#22c55e" : "#94a3b8", border: `1px solid ${r.mode === "live" ? "#22c55e55" : "#94a3b855"}`, fontSize: 9, padding: "1px 6px", borderRadius: 3, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{r.mode || "curated"}</span></Td>
                <Td><span style={{ color: "#94a3b8", fontSize: 11 }}>{fmtDate(r.started_at)}</span></Td>
                <Td><span style={{ color: "#cbd5e1", fontSize: 11 }}>{r.duration_ms}ms</span></Td>
                <Td><span style={{ color: "#cbd5e1", fontSize: 11 }}>{r.raw_fetched}</span></Td>
                <Td><strong style={{ color: r.inserted > 0 ? "#22c55e" : "#64748b", fontSize: 12 }}>+{r.inserted}</strong></Td>
                <Td><strong style={{ color: r.alerts_generated > 0 ? "#f97316" : "#64748b", fontSize: 12 }}>{r.alerts_generated || 0}</strong></Td>
                <Td>{r.status === "ok" ? <span style={{ color: "#22c55e", fontSize: 11 }}>OK</span> : <span style={{ color: "#ef4444", fontSize: 11 }}>ERROR</span>}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =====================================================
// ============== ALERTS TAB ===========================
// =====================================================
function AlertsTab({ alerts, alertStats, loadAlerts }) {
  const [statusFilter, setStatusFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");

  useEffect(() => {
    loadAlerts({ status: statusFilter, severity: severityFilter });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, severityFilter]);

  const updateAlert = async (id, body) => {
    try {
      await axios.patch(`${API}/alerts/${id}`, body, { withCredentials: true });
      toast.success("Alert updated");
      loadAlerts({ status: statusFilter, severity: severityFilter });
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 16 }}>
        <StatCard label="Total Alerts" value={alertStats.total || 0} color="#3b82f6" />
        <StatCard label="Open" value={alertStats.open || 0} color="#f97316" />
        <StatCard label="Unread" value={alertStats.unread || 0} color="#facc15" />
        <StatCard label="Last 24h" value={alertStats.last_24h || 0} color="#a855f7" />
        <StatCard label="Critical Open" value={alertStats.critical_open || 0} color="#dc2626" />
      </div>

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 14, marginBottom: 16 }}>
        <div className="flex items-center gap-3">
          <Bell size={14} style={{ color: "#94a3b8" }} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="alert-status-filter" style={{ ...selectStyle, width: 150 }}>
            <option value="">All statuses</option><option value="open">Open</option><option value="dismissed">Dismissed</option><option value="resolved">Resolved</option>
          </select>
          <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} data-testid="alert-severity-filter" style={{ ...selectStyle, width: 150 }}>
            <option value="">All severities</option><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option>
          </select>
        </div>
      </div>

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#080c12" }}><Th>Severity</Th><Th>Score</Th><Th>Customer</Th><Th>Matched Watchlist Entry</Th><Th>Source</Th><Th>Case</Th><Th>Created</Th><Th>Actions</Th></tr></thead>
          <tbody>
            {alerts.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#64748b", fontSize: 13 }}>No alerts. Run a Fuzzy Screen to raise one.</td></tr>
            ) : alerts.map((a) => (
              <tr key={a.id} style={{ borderTop: "1px solid #1e2530", background: a.is_read ? "transparent" : "#0d1722" }} data-testid={`alert-row-${a.id}`}>
                <Td><SeverityBadge severity={a.severity} /></Td>
                <Td><ScoreBar score={a.match_score} /></Td>
                <Td><strong style={{ color: "#f1f5f9", fontSize: 12 }}>{a.customer_name}</strong></Td>
                <Td><span style={{ color: "#cbd5e1", fontSize: 12 }}>{a.watchlist_entity_name}</span><br/><span style={{ color: "#64748b", fontSize: 10 }}>{a.watchlist_reason?.slice(0, 60)}</span></Td>
                <Td><SourceBadge source={a.watchlist_source} /></Td>
                <Td>{a.case_number ? <span style={{ color: "#22c55e", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{a.case_number}</span> : <span style={{ color: "#64748b", fontSize: 11 }}>—</span>}</Td>
                <Td><span style={{ color: "#94a3b8", fontSize: 11 }}>{new Date(a.created_at).toLocaleString()}</span></Td>
                <Td>
                  <div className="flex gap-1">
                    {a.status === "open" && (
                      <>
                        <button onClick={() => updateAlert(a.id, { status: "resolved" })} title="Resolve" data-testid={`alert-resolve-${a.id}`} style={{ background: "rgba(34,197,94,0.10)", border: "1px solid #22c55e", borderRadius: 4, color: "#22c55e", padding: "4px 6px", fontSize: 10, cursor: "pointer" }}><CheckCircle2 size={11} /></button>
                        <button onClick={() => updateAlert(a.id, { status: "dismissed" })} title="Dismiss" data-testid={`alert-dismiss-${a.id}`} style={{ background: "rgba(148,163,184,0.10)", border: "1px solid #94a3b8", borderRadius: 4, color: "#94a3b8", padding: "4px 6px", fontSize: 10, cursor: "pointer" }}><XCircle size={11} /></button>
                      </>
                    )}
                    {!a.is_read && (
                      <button onClick={() => updateAlert(a.id, { is_read: true })} title="Mark read" style={{ background: "rgba(59,130,246,0.10)", border: "1px solid #3b82f6", borderRadius: 4, color: "#3b82f6", padding: "4px 6px", fontSize: 10, cursor: "pointer" }}>•</button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// =====================================================
// ============== ROOT PAGE ============================
// =====================================================
export default function WatchlistPage() {
  const [tab, setTab] = useState("entries");
  const [stats, setStats] = useState({ total: 0, by_source: {}, by_type: {} });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [alerts, setAlerts] = useState([]);
  const [alertStats, setAlertStats] = useState({});

  const loadStats = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/stats`, { withCredentials: true }); setStats(data); }
    catch (err) { console.error("[Watchlist] Failed to load stats:", err); }
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      if (searchQ.trim().length >= 2) {
        const { data } = await axios.get(`${API}/search`, { params: { q: searchQ, limit: 200 }, withCredentials: true });
        setEntries(data.matches || []);
      } else {
        const params = { limit: 200 };
        if (sourceFilter) params.source = sourceFilter;
        if (typeFilter) params.entity_type = typeFilter;
        const { data } = await axios.get(`${API}/entries`, { params, withCredentials: true });
        setEntries(data.entries || []);
      }
    } catch (e) { toast.error("Failed to load entries"); }
    finally { setLoading(false); }
  }, [searchQ, sourceFilter, typeFilter]);

  const loadAlerts = useCallback(async (filters = {}) => {
    try {
      const params = { limit: 100 };
      if (filters.status) params.status = filters.status;
      if (filters.severity) params.severity = filters.severity;
      const [a, s] = await Promise.all([
        axios.get(`${API}/alerts`, { params, withCredentials: true }),
        axios.get(`${API}/alerts/stats`, { withCredentials: true }),
      ]);
      setAlerts(a.data.alerts || []); setAlertStats(s.data || {});
    } catch (err) { console.error("[Watchlist] Failed to load alerts:", err); }
  }, []);

  useEffect(() => { loadStats(); loadAlerts(); }, [loadStats, loadAlerts]);
  useEffect(() => { const t = setTimeout(() => loadEntries(), 300); return () => clearTimeout(t); }, [loadEntries]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/seed`, {}, { withCredentials: true });
      toast.success(`Seeded ${data.inserted} entries (${data.skipped} already existed)`);
      loadStats(); loadEntries();
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
    finally { setSeeding(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this watchlist entry?")) return;
    try { await axios.delete(`${API}/entries/${id}`, { withCredentials: true }); toast.success("Entry deleted"); loadStats(); loadEntries(); }
    catch { toast.error("Delete failed"); }
  };

  const exportCSV = () => {
    const headers = ["source", "entity_name", "entity_type", "pan", "cin", "reason", "order_date", "order_ref"];
    const rows = entries.map((e) => headers.map((h) => `"${(e[h] || "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = headers.join(",") + "\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `watchlist-india-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const TABS = [
    { id: "entries",    label: "Entries",      icon: Database,    count: stats.total },
    { id: "fuzzy",      label: "Fuzzy Screen", icon: Zap,         count: null },
    { id: "collectors", label: "Collectors",   icon: PlayCircle,  count: COLLECTOR_SOURCES.length },
    { id: "alerts",     label: "Alerts",       icon: AlertTriangle, count: alertStats.open || 0 },
  ];

  return (
    <div data-testid="watchlist-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <ShieldAlert style={{ width: 26, height: 26, color: "#dc2626" }} />
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0, color: "#f1f5f9" }} data-testid="page-title">India Watchlist Master</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSeed} disabled={seeding} data-testid="watchlist-seed-btn" style={{ ...btnGhost, borderColor: "#2563eb", color: "#2563eb" }}>
            {seeding ? <Loader2 className="animate-spin" size={12} /> : <Database size={12} />} Seed Defaults
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="watchlist-add-btn" style={btnPrimary}><Plus size={12} /> Add Entry</button>
        </div>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 16 }}>
        Persistent watchlist — automated collectors · fuzzy matching · auto-alerts &amp; case generation across SEBI, MHA UAPA, ED, CBI, SFIO, RBI, OpenSanctions, GSTIN, MCA.
      </p>

      <CollectorFreshnessBanner collectorKey="watchlist" />

      {/* Tabs */}
      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #1e2530", marginBottom: 18 }}>
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              style={{
                background: active ? "#0d1117" : "transparent", color: active ? "#f1f5f9" : "#94a3b8",
                border: "none", borderBottom: active ? "2px solid #2563eb" : "2px solid transparent",
                padding: "10px 18px", fontWeight: active ? 700 : 500, fontSize: 13, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 8,
              }}>
              <Icon size={14} /> {t.label}
              {t.count != null && t.count > 0 && (
                <span style={{ background: active ? "#2563eb" : "#1e2530", color: active ? "white" : "#94a3b8", borderRadius: 10, padding: "0 8px", fontSize: 10, fontWeight: 700 }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {tab === "entries"   && <EntriesTab {...{ stats, entries, loading, searchQ, setSearchQ, sourceFilter, setSourceFilter, typeFilter, setTypeFilter, onDelete: handleDelete, onExport: exportCSV, onSeedDefaults: handleSeed, seeding }} />}
      {tab === "fuzzy"     && <FuzzyScreenTab refreshAlerts={loadAlerts} />}
      {tab === "collectors"&& <CollectorsTab />}
      {tab === "alerts"    && <AlertsTab {...{ alerts, alertStats, loadAlerts }} />}

      {showAdd && <AddEntryModal onClose={() => setShowAdd(false)} onSaved={() => { loadStats(); loadEntries(); }} />}
    </div>
  );
}

function Th({ children }) {
  return <th style={{ textAlign: "left", padding: "10px 12px", color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>{children}</th>;
}
function Td({ children }) {
  return <td style={{ padding: "10px 12px", verticalAlign: "middle" }}>{children}</td>;
}
